import type { DistributionGroup } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import "./AccountsPanel.scss";
import { CheckBox, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { DisplayAccountTreeNode } from "./DistributionListPanel";

interface AccountsPanelProps {
  accountTree: DisplayAccountTreeNode;
  group?: DistributionGroup;
  onGroupChanged?: (group: DistributionGroup) => void;
}

export const isMemeberOfGroup = (
  id: string,
  group: DistributionGroup
): boolean => {
  return group.members.some(member => member.memberId === id);
};

export const applyGroupChange = (id: string, group: DistributionGroup) => {
  const clonedGroup = JSON.parse(JSON.stringify(group));
  if (isMemeberOfGroup(id, clonedGroup)) {
    clonedGroup.members = clonedGroup.members.filter(
      member => member.memberId !== id
    );
  } else {
    // Logic to add member to group
    clonedGroup.members.push({
      id: `member-${Date.now()}`, // Generate a unique ID for the member
      memberId: id,
      distributionListId: null // Assuming distributionListId is not needed here
    });
  }
  return clonedGroup;
};

export const buildAccountTree = (
  accountTree: DisplayAccountTreeNode,
  group: DistributionGroup,
  onGroupChanged: (group: DistributionGroup) => void
) => {
  return (
    <div className="account-groups">
      <div className="account-group-control">
        <Typography variant={"h3"}>{Locale.getText("ACCOUNTS")}</Typography>
        <div className="account-group-name">
          <CheckBox
            checked={isMemeberOfGroup(accountTree.id, group)}
            disabled={accountTree.disabled || false}
            label={accountTree.name}
            value={accountTree.id}
            onChange={() => {
              const updatedGroup = applyGroupChange(accountTree.id, group);
              onGroupChanged(updatedGroup);
            }}
          />
        </div>
        <div className="account-group-list">
          {listGroupAccounts(accountTree, group, onGroupChanged)}
        </div>
      </div>
    </div>
  );
};

export const listGroupAccounts = (
  accountTree: DisplayAccountTreeNode,
  group: DistributionGroup,
  onGroupChanged: (group: DistributionGroup) => void
) => {
  return accountTree.children.map(c => {
    const child = c as DisplayAccountTreeNode;
    if (child.disabled) {
      return null;
    }
    return (
      <div className="account-group-child" key={child.id}>
        <CheckBox
          checked={isMemeberOfGroup(child.id, group)}
          label={child.name}
          value={child.id}
          onChange={() => {
            const updatedGroup = applyGroupChange(child.id, group);
            onGroupChanged(updatedGroup);
          }}
        />
      </div>
    );
  });
};

export const AccountsPanel = ({
  accountTree,
  group,
  onGroupChanged
}: AccountsPanelProps) => {
  return (
    <div className="accounts-panel">
      <div className="panel account-list">
        {group &&
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          buildAccountTree(accountTree, group, onGroupChanged || (() => {}))}
      </div>
    </div>
  );
};
